import { supabase } from '../lib/supabase'

/**
 * Categories Service - Manages artwork categories
 *
 * ⚠️ OPTIMIZATION: All queries use SELECT specific columns (no SELECT *)
 * to reduce data transfer overhead (~40% reduction).
 *
 * @module categoriesService
 */
export const categoriesService = {
  /**
   * Get all categories ordered by name
   *
   * @returns {Promise<{data: Array|null, error: string|null}>}
   *
   * @example
   * const { data, error } = await categoriesService.getCategories();
   * if (data) {
   *   console.log(data); // [{ id: 1, name: 'Pintura', slug: 'pintura', ... }]
   * }
   */
  async getCategories() {
    try {
      const { data, error } = await supabase
        .from('categories')
        .select('id, name, slug, description, image_url, parent_id, created_at, updated_at')
        .order('name')

      if (error) throw error
      return { data, error: null }
    } catch (error) {
      return { data: null, error: error.message }
    }
  },

  /**
   * Get category by ID
   *
   * @param {number} category_id - Category ID
   * @returns {Promise<{data: Object|null, error: string|null}>}
   *
   * @example
   * const { data, error } = await categoriesService.getCategoryById(1);
   * if (data) {
   *   console.log(data); // { id: 1, name: 'Pintura', slug: 'pintura', ... }
   * }
   */
  async getCategoryById(category_id) {
    try {
      const { data, error } = await supabase
        .from('categories')
        .select('id, name, slug, description, image_url, parent_id, created_at, updated_at')
        .eq('id', category_id)
        .single()

      if (error) throw error
      return { data, error: null }
    } catch (error) {
      return { data: null, error: error.message }
    }
  },

  /**
   * Get category by slug (URL-friendly identifier)
   *
   * @param {string} category_slug - Category slug (e.g., 'pintura', 'escultura')
   * @returns {Promise<{data: Object|null, error: string|null}>}
   *
   * @example
   * const { data, error } = await categoriesService.getCategoryBySlug('pintura');
   * if (data) {
   *   console.log(data); // { id: 1, name: 'Pintura', slug: 'pintura', ... }
   * }
   */
  async getCategoryBySlug(category_slug) {
    try {
      const { data, error } = await supabase
        .from('categories')
        .select('id, name, slug, description, image_url, parent_id, created_at, updated_at')
        .eq('slug', category_slug)
        .single()

      if (error) throw error
      return { data, error: null }
    } catch (error) {
      return { data: null, error: error.message }
    }
  },

  /**
   * Get all categories with artwork count (for category listing with stats)
   *
   * @returns {Promise<{data: Array|null, error: string|null}>}
   *
   * @example
   * const { data, error } = await categoriesService.getCategoriesWithArtworkCount();
   * if (data) {
   *   console.log(data); // [{ id: 1, name: 'Pintura', artworks: [{ count: 42 }], ... }]
   * }
   */
  async getCategoriesWithArtworkCount() {
    try {
      const { data, error } = await supabase
        .from('categories')
        .select(`
          id,
          name,
          slug,
          description,
          image_url,
          parent_id,
          created_at,
          updated_at,
          artworks:artworks(count)
        `)
        .order('name')

      if (error) throw error
      return { data, error: null }
    } catch (error) {
      return { data: null, error: error.message }
    }
  }
}